/**
 * Vincent B1012 (Eric 2026-02-26 SPEC STRICK PROD) — Modale d'historique
 * des imports CSV/XLSX avec bouton rollback en 1 clic.
 *
 * Props :
 *   - open (bool)
 *   - onClose (fn)
 *   - type (string)             : "jargon" | "video_tags" | "all"
 *   - onRolledBack (fn)         : appelé après un rollback réussi
 *   - dataTestidPrefix (string) : préfixe des data-testid
 *   - lang (string)             : "fr" | "en"
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ImportHistoryModal({
  open,
  onClose,
  type = "all",
  onRolledBack,
  dataTestidPrefix = "import-history",
  lang = "fr",
}) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rollbackingId, setRollbackingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/imports/batches?type=${encodeURIComponent(type)}`,
        { withCredentials: true },
      );
      setBatches(data?.batches || []);
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRollback = async (batch) => {
    const msg = lang === "fr"
      ? `Annuler cet import de ${batch.summary?.added ?? "?"} entrée(s) ? Cette action retire les termes ou tags ajoutés le ${new Date(batch.imported_at).toLocaleString("fr-CA")}.`
      : `Undo this import of ${batch.summary?.added ?? "?"} entry/entries? This removes the terms or tags added on ${new Date(batch.imported_at).toLocaleString("en-CA")}.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setRollbackingId(batch.id);
    try {
      const { data } = await axios.post(
        `${API}/api/imports/batches/${batch.id}/rollback`,
        {},
        { withCredentials: true },
      );
      toast.success(lang === "fr"
        ? `Import annulé (${data?.reverted ?? 0} entrée(s) retirée(s)).`
        : `Import undone (${data?.reverted ?? 0} entry/entries removed).`);
      await load();
      if (onRolledBack) onRolledBack(batch);
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setRollbackingId(null);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid={`${dataTestidPrefix}-overlay`}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby={`${dataTestidPrefix}-title`}
        data-testid={`${dataTestidPrefix}-modal`}
      >
        <header className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <h2 id={`${dataTestidPrefix}-title`} className="text-lg font-semibold text-zinc-900">
            {lang === "fr" ? "Historique des imports" : "Import history"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-900 text-xl leading-none"
            aria-label={lang === "fr" ? "Fermer" : "Close"}
            data-testid={`${dataTestidPrefix}-close`}
          >
            ×
          </button>
        </header>
        <div className="px-5 py-4">
          {loading && (
            <p className="text-sm italic text-zinc-500" data-testid={`${dataTestidPrefix}-loading`}>
              {lang === "fr" ? "Chargement…" : "Loading…"}
            </p>
          )}
          {!loading && batches.length === 0 && (
            <p className="text-sm italic text-zinc-500" data-testid={`${dataTestidPrefix}-empty`}>
              {lang === "fr" ? "Aucun import enregistré." : "No import recorded."}
            </p>
          )}
          {!loading && batches.length > 0 && (
            <ul className="flex flex-col gap-2" data-testid={`${dataTestidPrefix}-list`}>
              {batches.map((b) => (
                <li
                  key={b.id}
                  className={`border rounded p-3 text-sm ${b.rolled_back ? "border-zinc-200 bg-zinc-50 opacity-70" : "border-emerald-300 bg-emerald-50"}`}
                  data-testid={`${dataTestidPrefix}-item-${b.id}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${b.type === "jargon" ? "bg-sky-100 text-sky-800" : "bg-violet-100 text-violet-800"}`}
                          data-testid={`${dataTestidPrefix}-item-type-${b.id}`}
                        >
                          {b.type === "jargon"
                            ? (lang === "fr" ? "dictionnaire" : "dictionary")
                            : (lang === "fr" ? "tags vidéos" : "video tags")}
                        </span>
                        <span className="font-mono text-xs text-zinc-700 truncate" title={b.admin_email || ""}>
                          {b.admin_name || b.admin_email || "?"}
                        </span>
                        <span className="text-[11px] font-mono text-zinc-500">
                          {b.imported_at ? new Date(b.imported_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA") : "?"}
                        </span>
                      </div>
                      <div className="text-xs text-zinc-700 mt-1 font-mono">
                        {lang === "fr" ? "Ajouté : " : "Added: "}
                        <b>{b.summary?.added ?? 0}</b>
                        {b.type === "video_tags" && (
                          <>
                            {" · "}
                            {lang === "fr" ? "vidéos : " : "videos: "}
                            <b>{b.summary?.videos_updated ?? 0}</b>
                          </>
                        )}
                        {b.type === "jargon" && (
                          <>
                            {" · "}
                            {lang === "fr" ? "doublons : " : "duplicates: "}
                            <b>{b.summary?.skipped_duplicates ?? 0}</b>
                          </>
                        )}
                      </div>
                      {b.rolled_back && (
                        <div className="text-[11px] text-zinc-600 mt-1 italic" data-testid={`${dataTestidPrefix}-item-rolled-${b.id}`}>
                          {lang === "fr" ? "Annulé le " : "Rolled back on "}
                          {b.rolled_back_at ? new Date(b.rolled_back_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA") : "?"}
                          {b.rolled_back_by && ` — ${b.rolled_back_by}`}
                          {typeof b.rolled_back_count === "number" && ` (${b.rolled_back_count})`}
                        </div>
                      )}
                    </div>
                    {!b.rolled_back && (
                      <button
                        type="button"
                        onClick={() => handleRollback(b)}
                        disabled={rollbackingId === b.id}
                        className="text-xs font-semibold px-2.5 py-1 rounded bg-white border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                        data-testid={`${dataTestidPrefix}-rollback-${b.id}`}
                        title={lang === "fr" ? "Annuler cet import (retire les entrées ajoutées)" : "Undo this import (removes added entries)"}
                      >
                        {rollbackingId === b.id
                          ? (lang === "fr" ? "Annulation…" : "Undoing…")
                          : (lang === "fr" ? "Annuler l'import" : "Undo import")}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <footer className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold px-3 py-1.5 rounded bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
            data-testid={`${dataTestidPrefix}-done`}
          >
            {lang === "fr" ? "Fermer" : "Close"}
          </button>
        </footer>
      </div>
    </div>
  );
}
